import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import type { SpaceBindingsResponse, SpaceResourceAction } from './space-resources';

// Client data layer for the Space Resources card: `/api/space-bindings` (symphony-bff ListSpaceBindings /
// UpdateSpaceBinding). The rules about what the answer MEANS live in `lib/space-resources.ts`.

export async function fetchSpaceBindings(workspaceId: string): Promise<SpaceBindingsResponse> {
  const res = await apiFetch(`${API_BASE}/api/space-bindings?workspaceId=${encodeURIComponent(workspaceId)}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as SpaceBindingsResponse;
}

/** The card's read. A failed read leaves `data` undefined, and the card then renders nothing. */
export function useSpaceBindings(workspaceId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.spaceBindings(workspaceId ?? ''),
    queryFn: () => fetchSpaceBindings(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
  });
}

export type SpaceBindingUpdate = {
  workspaceId: string;
  action: Extract<SpaceResourceAction, 'SET_MAIN' | 'UNBIND' | 'MOVE_TO_SPACE' | 'CONFIRM' | 'DISMISS'>;
  bindingId: string;
  targetWorkspaceId?: string;
};

/** The answer to one UpdateSpaceBinding: its `outcome`, and on REFUSED the server's `message` (empty when it gave none). */
export interface SpaceBindingResult {
  outcome?: string;
  message: string;
}

/**
 * POSTs one update. A transport failure resolves to `{ outcome: undefined }`, which callers read as the generic error.
 * On UPDATED / UNCHANGED the Space's list and the workspaces (which carry the website tile) are re-read — a moved,
 * re-mained or added website changes what the header shows, and a move changes the target Space's list too.
 */
async function postSpaceBinding(body: Record<string, unknown>, workspaceId: string, targetWorkspaceId?: string): Promise<SpaceBindingResult> {
  let result: SpaceBindingResult = { outcome: undefined, message: '' };
  try {
    const res = await apiFetch(`${API_BASE}/api/space-bindings`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return result;
    const json = (await res.json().catch(() => ({}))) as { outcome?: string; message?: string };
    result = { outcome: json.outcome, message: typeof json.message === 'string' ? json.message.trim() : '' };
  } catch {
    return result;
  }
  if (result.outcome === 'UPDATED' || result.outcome === 'UNCHANGED') {
    void queryClient.invalidateQueries({ queryKey: queryKeys.spaceBindings(workspaceId) });
    if (targetWorkspaceId) void queryClient.invalidateQueries({ queryKey: queryKeys.spaceBindings(targetWorkspaceId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces });
  }
  return result;
}

/**
 * One UpdateSpaceBinding on a binding. Resolves to the server's `outcome` (a refusal is an outcome, not a throw);
 * a transport failure resolves to `undefined`, which the caller reads as the generic error.
 */
export async function updateSpaceBinding(update: SpaceBindingUpdate): Promise<string | undefined> {
  return (await postSpaceBinding(update, update.workspaceId, update.targetWorkspaceId)).outcome;
}

/**
 * "+ Add website, page or listing": UpdateSpaceBinding ADD_LINK with the pasted `url` (already normalised by
 * `presenceAddLinkUrl`). The server derives the provider and kind, and answers UPDATED, or REFUSED with a message.
 */
export async function addSpaceLink(workspaceId: string, url: string): Promise<SpaceBindingResult> {
  return postSpaceBinding({ workspaceId, action: 'ADD_LINK', url }, workspaceId);
}
